export interface PlanDetails {
  quantity: number;
  isAnnual: boolean;
  priceId: string;
}

export interface Coupon {
  id: string;
  name?: string;
  amount_off?: number;
  percent_off?: number;
  [key: string]: any;
}

export interface PlanObject {
  free?: PlanDetails;
  plus?: PlanDetails;
  growth?: PlanDetails;
  enterprise?: PlanDetails;
  customIntentTopic?: PlanDetails;
  emailEnrichment?: PlanDetails;
  coupon?: Coupon;
  planName?: string;
  isAnnualPlan?: boolean;
  hasPaidPlan?: boolean;
  [key: string]: PlanDetails | Coupon | string | boolean | undefined;
}

interface SubscriptionItem {
  price: {
    product: string;
    id: string;
    recurring: {
      interval: 'month' | 'year';
    };
  };
  quantity: number;
}

interface SubscriptionData {
  items?: {
    data?: SubscriptionItem[];
  };
  discount?: {
    coupon?: Coupon;
  };
}

interface OrgState {
  subscription?: SubscriptionData;
}

interface ProductObj {
  product: string;
  quantity: number;
  isAnnual: boolean;
  priceId: string;
}

interface StripeProduct {
  productId: string;
  [key: string]: any;
}

interface StripeProducts {
  [key: string]: StripeProduct;
}

export const usePlan = async (): Promise<PlanObject> => {
  const config = useRuntimeConfig();
  const org = useState<OrgState>('org');

  const currentProductObjs: ProductObj[] =
    org?.value?.subscription?.items?.data?.map((item: SubscriptionItem) => {
      return {
        product: item.price.product,
        quantity: item.quantity,
        isAnnual: item.price.recurring.interval === 'year',
        priceId: item.price.id,
      };
    }) || [];

  const currentPlanObj: PlanObject = {};

  currentProductObjs.forEach((productObj: ProductObj) => {
    const product = findProduct(config.public.stripeProducts as StripeProducts, productObj.product);
    if (productObj.quantity > 0 && product) {
      currentPlanObj[product] = {
        quantity: productObj.quantity,
        isAnnual: productObj.isAnnual,
        priceId: productObj.priceId,
      };
    }
  });

  currentPlanObj.planName = calculatePlanName(currentPlanObj);
  currentPlanObj.isAnnualPlan = getIsAnnualPlan(currentPlanObj);
  currentPlanObj.hasPaidPlan = hasPaidPlan(currentPlanObj);

  if (org?.value?.subscription?.discount) {
    const discount = org.value.subscription.discount;

    // Check if the discount is a coupon
    if (discount.coupon) {
      currentPlanObj.coupon = discount.coupon;
    }
  }

  return currentPlanObj;
};

const hasPaidPlan = (plan: PlanObject) => {
  return !!(plan?.plus || plan?.growth || plan?.enterprise);
};

const getIsAnnualPlan = (plan: PlanObject) => {
  return plan?.plus?.isAnnual || plan?.growth?.isAnnual || plan?.enterprise?.isAnnual || false;
};

const calculatePlanName = (plan: PlanObject) => {
  return plan?.free
    ? 'free'
    : plan?.plus
      ? 'plus'
      : plan?.growth
        ? 'growth'
        : plan?.enterprise
          ? 'enterprise'
          : 'unknown';
};

const findProduct = (stripeProducts: StripeProducts, productId: string): string | undefined => {
  let product: string | undefined;
  Object.keys(stripeProducts).forEach((key: string) => {
    if (stripeProducts[key].productId === productId) {
      product = key;
    }
  });
  return product;
};
